import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { getCabinetCustomerPortal, sendCabinetCustomerPortalChat } from "./api";
import { CQ_FIELD, Notice, cqStatusClass } from "./ui";
import { useIsDarkMode } from "../../theme/ThemeContext";

const getStatusClass = cqStatusClass;

const formatStatusLabel = (status) =>
  String(status || "draft")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const initials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

// Shared card + label styles for the portal. Customers open this on their
// own phones, so it follows their OS appearance like the rest of the site.
const CARD = "app-card rounded-2xl border";
const FIELD_LABEL = "app-eyebrow mb-0.5";

function BrandBar({ customerName }) {
  const isDark = useIsDarkMode();
  return (
    <header className="app-chrome border-b">
      <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3 sm:gap-4 sm:px-6">
        <a
          href="https://bigclumber.com/"
          className="shrink-0 rounded-lg"
          aria-label="Big C Lumber website"
        >
          <img
            src={
              isDark
                ? require("../../assets/BigCWhite.png")
                : require("../../assets/BigC.png")
            }
            alt="Big C Lumber"
            className="h-9 w-auto sm:h-10"
          />
        </a>
        <span className="h-8 w-px shrink-0 bg-[color:var(--app-divider)]" aria-hidden="true" />
        <div className="min-w-0">
          <p className="font-semibold leading-tight">Cabinet Quote Portal</p>
          <p className="app-muted truncate text-xs">{customerName ? `Prepared for ${customerName}` : "Your personalized quote"}</p>
        </div>
      </div>
    </header>
  );
}

function CabinetQuoteCustomerPortal() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [chatName, setChatName] = useState("");
  const [chatDraft, setChatDraft] = useState("");
  const [chatAttachments, setChatAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [imageModal, setImageModal] = useState({ open: false, src: "", name: "" });
  const chatScrollRef = useRef(null);

  const externalMessages = useMemo(() => {
    return Array.isArray(quote?.chats?.external) ? quote.chats.external : [];
  }, [quote]);
  const quotePdfUrl = `/api/forms/pdf?id=${encodeURIComponent(quote?._id || "")}&source=cabinet_quote`;
  const customerName = String(quote?.customer?.name || "").trim();

  const loadQuote = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getCabinetCustomerPortal(token);
      setQuote(data);
      setChatName(String(data?.customer?.name || "Customer").trim() || "Customer");
    } catch (loadError) {
      setError(loadError?.message || "Unable to load customer portal.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuote();
  }, [token]);

  useEffect(() => {
    if (!chatScrollRef.current) return;
    chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [externalMessages]);

  const isFromCustomer = (message) =>
    customerName &&
    String(message?.user_name || "").trim().toLowerCase() === customerName.toLowerCase();

  const onSendMessage = async () => {
    const messageText = String(chatDraft || "").trim();
    if (!messageText && chatAttachments.length === 0) {
      return;
    }

    setError("");
    setSending(true);
    try {
      await sendCabinetCustomerPortalChat(token, {
        text: messageText,
        user_name: String(chatName || "").trim() || customerName || "Customer",
        attachments: chatAttachments,
      });
      setChatDraft("");
      setChatAttachments([]);
      await loadQuote();
    } catch (sendError) {
      setError(sendError?.message || "Failed to send chat message.");
    } finally {
      setSending(false);
    }
  };

  const onSelectAttachments = (event) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length === 0) {
      return;
    }

    const allowedMimeTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"];
    const maxBytes = 10 * 1024 * 1024;
    const invalid = selected.find((file) => !allowedMimeTypes.includes(String(file.type || "").toLowerCase()));
    if (invalid) {
      setError("Only image and PDF files are allowed.");
      event.target.value = "";
      return;
    }

    const tooLarge = selected.find((file) => Number(file.size || 0) > maxBytes);
    if (tooLarge) {
      setError("Each attachment must be 10MB or smaller.");
      event.target.value = "";
      return;
    }

    setError("");
    setChatAttachments((prev) => [...prev, ...selected].slice(0, 6));
    event.target.value = "";
  };

  const removeAttachment = (index) => {
    setChatAttachments((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const openImageModal = (attachment) => {
    setImageModal({
      open: true,
      src: String(attachment?.url || ""),
      name: String(attachment?.name || "Image attachment"),
    });
  };

  const closeImageModal = () => {
    setImageModal({ open: false, src: "", name: "" });
  };

  if (loading) {
    return (
      <div className="app-page min-h-screen">
        <BrandBar />
        <div className="mx-auto max-w-5xl space-y-4 px-4 py-6 sm:px-6" role="status" aria-label="Loading your quote">
          <div className="app-skeleton h-32 !rounded-2xl" />
          <div className="app-skeleton h-96 !rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="app-page min-h-screen">
        <BrandBar />
        <div className="mx-auto max-w-md px-4 py-16 text-center sm:px-6">
          <div className={`${CARD} p-8`}>
            <span className="app-icon-tile mx-auto mb-4 h-14 w-14 rounded-2xl bg-slate-500/10 text-slate-500 dark:text-slate-400">
              <LockOutlinedIcon />
            </span>
            <h1 className="text-lg font-semibold">This quote link is unavailable</h1>
            <p className="app-muted mt-2 text-sm leading-relaxed">
              {error || "The link may have expired or is incorrect. Please contact your sales representative for an updated link."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page min-h-screen">
      <BrandBar customerName={customerName} />

      <main className="mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        {error && (
          <Notice tone="rose" role="alert">{error}</Notice>
        )}

        {/* Quote summary */}
        <section className={`${CARD} p-5 sm:p-6`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="app-eyebrow mb-1">Cabinet Quote</p>
              <h1 className="text-2xl font-bold tracking-tight sm:text-[1.75rem] sm:leading-9">{quote.title || "Cabinet Quote"}</h1>
              {quote.quote_number && <p className="app-muted mt-0.5 text-sm tabular-nums">Quote #{quote.quote_number}</p>}
            </div>
            <span className={`inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium ${getStatusClass(quote.status)}`}>
              <span className="h-2 w-2 rounded-full bg-current opacity-70" />
              {formatStatusLabel(quote.status)}
            </span>
          </div>

          <dl className="app-divider mt-5 grid grid-cols-1 gap-4 border-t pt-5 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className={FIELD_LABEL}>Prepared for</dt>
              <dd className="font-medium">{quote?.customer?.name || "—"}</dd>
            </div>
            <div>
              <dt className={FIELD_LABEL}>Phone</dt>
              <dd className="font-medium tabular-nums">{quote?.customer?.phone || "—"}</dd>
            </div>
            <div>
              <dt className={FIELD_LABEL}>Email</dt>
              <dd className="break-all font-medium">{quote?.customer?.email || "—"}</dd>
            </div>
            {quote?.customer?.job_address && (
              <div className="sm:col-span-2 lg:col-span-3">
                <dt className={FIELD_LABEL}>Job Address</dt>
                <dd className="font-medium">{quote.customer.job_address}</dd>
              </div>
            )}
          </dl>
        </section>

        {/* The quote document */}
        <section className={`${CARD} overflow-hidden`}>
          <div className="app-divider flex flex-col gap-3 border-b px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div className="flex min-w-0 items-start gap-3">
              <span className="app-icon-tile h-10 w-10 rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-300">
                <PictureAsPdfOutlinedIcon fontSize="small" />
              </span>
              <div className="min-w-0">
                <h2 className="font-semibold">Your Quote</h2>
                <p className="app-muted text-sm">Review the full details of your cabinet quote below.</p>
              </div>
            </div>
            <a
              href={`${quotePdfUrl}&download=1`}
              className="app-btn app-btn-primary w-full shrink-0 sm:w-auto"
            >
              <DownloadIcon fontSize="small" />
              Download PDF
            </a>
          </div>
          {/* The PDF viewer stays a light surface in every theme. */}
          <iframe
            title="Your Cabinet Quote"
            src={quotePdfUrl}
            className="block h-[70vh] w-full bg-white sm:h-[44rem]"
          >
            Your browser does not support embedded PDFs.
          </iframe>
        </section>

        {/* Messages */}
        <section className={`${CARD} space-y-4 p-5 sm:p-6`}>
          <div className="flex items-start gap-3">
            <span className="app-icon-tile h-10 w-10 rounded-xl bg-sky-500/15 text-sky-600 dark:text-sky-300">
              <ChatBubbleOutlineIcon fontSize="small" />
            </span>
            <div className="min-w-0">
              <h2 className="font-semibold">Questions about your quote?</h2>
              <p className="app-muted text-sm">Message our team directly — we'll get back to you here.</p>
            </div>
          </div>

          <div ref={chatScrollRef} className="app-surface h-80 space-y-3 overflow-y-auto rounded-xl border p-3 sm:p-4" aria-live="polite">
            {externalMessages.map((message) => {
              const mine = isFromCustomer(message);
              return (
                <div key={message.id || `${message.user_name}_${message.created_at}`} className={`flex gap-2 ${mine ? "justify-end" : "justify-start"}`}>
                  {!mine && (
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-xs font-semibold text-amber-700 dark:text-amber-300" aria-hidden="true">
                      {initials(message.user_name)}
                    </div>
                  )}
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2 sm:max-w-[80%] ${mine ? "rounded-br-md bg-sky-600 text-white shadow-sm" : "app-card rounded-bl-md border"}`}>
                    <div className={`flex items-center justify-between gap-3 text-[11px] ${mine ? "text-sky-100" : "app-muted"}`}>
                      <span className="font-semibold">{mine ? "You" : message.user_name || "Our Team"}</span>
                      <span className="tabular-nums">{formatTimestamp(message.created_at)}</span>
                    </div>
                    {message.text && <p className={`mt-1 whitespace-pre-wrap break-words text-sm ${mine ? "text-white" : ""}`}>{message.text}</p>}
                    {(message.attachments || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(message.attachments || []).map((attachment) => {
                          const isImage = String(attachment?.kind || "") === "image";
                          if (isImage) {
                            return (
                              <button
                                key={attachment.id}
                                type="button"
                                className="block overflow-hidden rounded-xl border border-black/10 bg-white transition-opacity hover:opacity-90"
                                onClick={() => openImageModal(attachment)}
                                aria-label={`View ${attachment.name || "image attachment"}`}
                              >
                                <img src={attachment.url} alt={attachment.name || "Image attachment"} className="h-28 w-28 object-cover" />
                              </button>
                            );
                          }
                          return (
                            <a
                              key={attachment.id}
                              href={`${attachment.url}?download=1`}
                              target="_blank"
                              rel="noreferrer"
                              className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium ${mine ? "border-white/30 bg-white/15 text-white hover:bg-white/25" : "app-surface app-accent hover:bg-[color:var(--app-hover)]"}`}
                            >
                              📄 {attachment.name || "PDF attachment"}
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {externalMessages.length === 0 && (
              <div className="flex h-full items-center justify-center text-center">
                <p className="app-muted max-w-xs text-sm">No messages yet. Send us a note if you have any questions about your quote.</p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <input
                className={CQ_FIELD}
                value={chatName}
                onChange={(event) => setChatName(event.target.value)}
                placeholder="Your name"
                aria-label="Your name"
                autoComplete="name"
              />
              <textarea
                className={`${CQ_FIELD} sm:col-span-2`}
                value={chatDraft}
                rows={2}
                placeholder="Type your message…"
                aria-label="Your message"
                onChange={(event) => setChatDraft(event.target.value)}
              />
            </div>
            <div className="flex items-center justify-between gap-2">
              <label className="app-btn app-btn-secondary cursor-pointer">
                <AttachFileIcon fontSize="small" />
                Attach
                <input type="file" className="hidden" accept="application/pdf,image/*" multiple onChange={onSelectAttachments} />
              </label>
              <button
                type="button"
                className="app-btn app-btn-primary"
                onClick={onSendMessage}
                disabled={sending}
              >
                {sending ? "Sending…" : "Send Message"}
                {!sending && <SendIcon sx={{ fontSize: 16 }} />}
              </button>
            </div>
          </div>
          {chatAttachments.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {chatAttachments.map((file, index) => (
                <span key={`${file.name}_${index}`} className="app-chip max-w-full py-1 pr-1">
                  <span className="truncate">{file.name}</span>
                  <button
                    type="button"
                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-rose-700 hover:bg-rose-500/10 dark:text-rose-300"
                    onClick={() => removeAttachment(index)}
                    aria-label={`Remove ${file.name}`}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <p className="app-muted text-xs">Images or PDFs only, up to 10MB each.</p>
        </section>

        <footer className="app-muted space-y-1 pb-6 pt-2 text-center text-xs">
          <p>🔒 This is a secure, private quote portal prepared for you. Please don't share this link.</p>
          <p>© {new Date().getFullYear()} Big C Lumber</p>
        </footer>
      </main>

      {imageModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 glass:backdrop-blur-[6px]"
          style={{ backgroundColor: "var(--app-scrim)" }}
          onClick={closeImageModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={imageModal.name}
            className="app-glass app-glass-rim flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl border"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="app-divider flex items-center justify-between gap-3 border-b px-4 py-3">
              <p className="mr-3 truncate text-sm font-medium">{imageModal.name}</p>
              <div className="flex shrink-0 items-center gap-2">
                <a
                  href={`${imageModal.src}?download=1`}
                  className="app-btn app-btn-primary app-btn-sm"
                  download
                >
                  Download
                </a>
                <button type="button" className="app-btn app-btn-secondary app-btn-sm" onClick={closeImageModal}>
                  Close
                </button>
              </div>
            </div>
            <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-black/5 p-4 dark:bg-black/30">
              <img src={imageModal.src} alt={imageModal.name} className="max-h-[70vh] max-w-full rounded-lg object-contain" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CabinetQuoteCustomerPortal;
